/**
 * Implementation primitives for the trail layer — source + line
 * layer registration, paint composition. Split from
 * :file:`trail-layer.tsx` so the
 * ``react-refresh/only-export-components`` lint rule stays
 * satisfied (the ``.tsx`` file exports the React component
 * exclusively). Mirrors :file:`snapshot-layer-impl.ts` and
 * :file:`cluster-layer-impl.ts`.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - ADR-016 §5.
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 */
import type { ExpressionSpecification, GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";
import { isMapUsable } from "@/map/map-ready";
import { SNAPSHOT_LAYER_ID } from "@/map/snapshot-layer-impl";
import {
  TRAIL_LAYER_ID,
  TRAIL_LINE_OPACITY,
  TRAIL_LINE_WIDTH_PX,
  TRAIL_SOURCE_ID,
  trailItemsToFeatureCollection,
  trailLineColor,
  trailStatusFromItems,
} from "@/map/trail-styling";
import type { TrailItem } from "@/map/trail-types";

/**
 * Register the trail source + line layer on the live map.
 * Idempotent — subsequent calls update the source data via
 * :meth:`GeoJSONSource.setData` rather than re-adding.
 *
 * The line layer is inserted **below** the snapshot marker layer
 * via the ``beforeId`` argument to :meth:`Map.addLayer` so the
 * marker paints on top of the trail (the marker is the
 * current-state surface; the trail is the history-behind-it).
 *
 * Exported for unit testability. The :class:`TrailLayer` component
 * (in :file:`trail-layer.tsx`) owns the React-effect lifecycle.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function attachTrailLayer(map: MapLibreMap, items: readonly TrailItem[]): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const featureCollection = trailItemsToFeatureCollection(items);

  const existingSource = map.getSource(TRAIL_SOURCE_ID) as GeoJSONSource | undefined;
  if (existingSource) {
    existingSource.setData(featureCollection);
    // Re-resolve the line color in case the trail's most-recent
    // status changed (e.g., next refetch returns a degraded
    // sequence). The source-update + paint-update pair keeps the
    // visual story coherent.
    if (map.getLayer(TRAIL_LAYER_ID)) {
      const status = trailStatusFromItems(items);
      map.setPaintProperty(TRAIL_LAYER_ID, "line-color", trailLineColor(status));
    }
    return;
  }

  map.addSource(TRAIL_SOURCE_ID, {
    type: "geojson",
    data: featureCollection,
  });

  // Snapshot marker layer paints above the trail. ``beforeId`` is
  // a hint — MapLibre falls back to "on top" if the named layer
  // doesn't exist (e.g., snapshot layer detached during a teardown
  // race). Operator-visibility per CLAUDE.md §8.1: the trail still
  // renders even if the marker layer briefly disappears.
  const beforeId = map.getLayer(SNAPSHOT_LAYER_ID) ? SNAPSHOT_LAYER_ID : undefined;

  const status = trailStatusFromItems(items);
  map.addLayer(
    {
      id: TRAIL_LAYER_ID,
      type: "line",
      source: TRAIL_SOURCE_ID,
      // The line layer only paints LineString features — Point
      // features (1-item degenerate trails) are filtered out.
      // Defense-in-depth: the eligibility predicate also filters
      // these upstream.
      filter: ["==", ["geometry-type"], "LineString"],
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": trailLineColor(status),
        "line-width": TRAIL_LINE_WIDTH_PX,
        "line-opacity": TRAIL_LINE_OPACITY as ExpressionSpecification | number,
      },
    },
    beforeId,
  );
}

/**
 * Tear down the trail layer + source on unmount or selection-
 * clear.
 *
 * Calls :meth:`removeLayer` before :meth:`removeSource` per
 * MapLibre's documented ordering (a source can't be removed while
 * a layer references it).
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function detachTrailLayer(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(TRAIL_LAYER_ID)) {
    map.removeLayer(TRAIL_LAYER_ID);
  }
  if (map.getSource(TRAIL_SOURCE_ID)) {
    map.removeSource(TRAIL_SOURCE_ID);
  }
}
